import React, { useState, useEffect, useContext } from "react";
import { useParams, useHistory } from "react-router-dom";

import { toast } from "react-toastify";
import openSocket from "../../services/socket-io";
import clsx from "clsx";

import { Paper, makeStyles } from "@material-ui/core";

import ContactDrawer from "../ContactDrawer";
import MessageInput from "../MessageInput/";
import TicketHeader from "../TicketHeader";
import TicketInfo from "../TicketInfo";
import TicketActionButtons from "../TicketActionButtons";
import MessagesList from "../MessagesList";
import api from "../../services/api";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import { EditingMessageProvider } from "../../context/EditingMessage/EditingMessageContext";
import { MensagensPendentesProvider } from "../../context/MensagensPendentes/MensagensPendentesContext";
import toastError from "../../errors/toastError";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import { poderDeAdmin } from "../../utils/perfil";

const drawerWidth = 380;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100%",
    position: "relative",
    overflow: "hidden",
  },

  // O nome e os selos ocupam o espaco que sobrar e cortam com "..." — antes
  // a caixa tinha 50% fixo e o texto passava por cima dos icones da direita.
  ticketInfo: {
    flex: "1 1 auto",
    minWidth: 0,
    overflow: "hidden",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "80%",
      flexBasis: "80%",
    },
  },
  ticketActionButtons: {
    flex: "0 0 auto",
    marginLeft: "auto",
    display: "flex",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "100%",
      flexBasis: "100%",
      marginBottom: "5px",
    },
  },

  mainWrapper: {
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeft: "0",
    marginRight: -drawerWidth,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    [theme.breakpoints.down("sm")]: {
      marginRight: 0,
    },
  },

  mainWrapperShift: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
  },
}));

const Ticket = () => {
  const { ticketId } = useParams();
  const history = useHistory();
  const classes = useStyles();

  const { whatsApps } = useContext(WhatsAppsContext) || {};
  const { user } = useContext(AuthContext) || {};
  const ehAdmin = poderDeAdmin(user?.profile);

  // O atendimento foi tirado deste vendedor (atribuido a outra pessoa ou
  // devolvido para a fila): fecha a conversa na hora, sem precisar de F5.
  useEffect(() => {
    if (ehAdmin) return undefined;
    const socket = openSocket();
    const aoSerRemovido = (data) => {
      if (String(data?.ticketId) !== String(ticketId)) return;
      toast.info("Este atendimento foi passado para outra pessoa.");
      history.push("/atendimentos");
    };
    socket.on("ticketRemovido", aoSerRemovido);
    return () => socket.off("ticketRemovido", aoSerRemovido);
  }, [ticketId, ehAdmin, history]);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchTicket = async () => {
        try {
          const { data } = await api.get("/tickets/" + ticketId);

          setContact(data.contact);
          setTicket(data);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchTicket();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [ticketId, history]);

  useEffect(() => {
    const socket = openSocket();

    const onConnect = () => socket.emit("joinChatBox", ticketId);
    socket.on("connect", onConnect);
    if (socket.connected) {
      onConnect();
    }

    // Nomeados de propósito: socket.off("ticket") sem função apagaria também
    // o ouvinte da LISTA de tickets, que é quem pinta a etiqueta na linha do
    // cliente. Era por isso que a etiqueta só aparecia depois do F5 -- bastava
    // ter aberto uma conversa e voltado.
    const aoAtualizarTicket = (data) => {
      if (data.action === "update") {
        if (data.ticket?.id === +ticketId || data.ticket?.id === ticketId) {
          setTicket(data.ticket);
        }
      }

      if (data.action === "delete" && data.deleted) {
        if (data.ticketId === +ticketId || data.ticketId === ticketId) {
          toast.success("Ticket excluído com sucesso.");
          history.push("/atendimentos");
        }
      }
    };

    const aoAtualizarContato = (data) => {
      if (data.action === "update") {
        setContact((prevState) => {
          if (prevState.id === data.contact?.id) {
            return { ...prevState, ...data.contact };
          }
          return prevState;
        });
      }
    };

    // A etiqueta esta copiada dentro do ticket. Quando alguem muda a cor ou o
    // nome dela na pagina de Etiquetas, o evento traz so a etiqueta — a copia
    // que esta aqui no cabecalho continuaria com o valor antigo ate o F5.
    const aoAtualizarEtiqueta = (data) => {
      setTicket((anterior) => {
        if (!anterior || !Array.isArray(anterior.tags)) return anterior;

        if ((data.action === "update" || data.action === "create") && data.tag) {
          if (!anterior.tags.some((t) => t.id === data.tag.id)) return anterior;

          return {
            ...anterior,
            tags: anterior.tags.map((t) =>
              t.id === data.tag.id ? { ...t, ...data.tag } : t
            ),
          };
        }

        if (data.action === "delete" && data.tagId) {
          if (!anterior.tags.some((t) => t.id === data.tagId)) return anterior;

          return {
            ...anterior,
            tags: anterior.tags.filter((t) => t.id !== data.tagId),
          };
        }

        return anterior;
      });
    };

    socket.on("ticket", aoAtualizarTicket);
    socket.on("contact", aoAtualizarContato);
    socket.on("tag", aoAtualizarEtiqueta);

    return () => {
      socket.off("connect", onConnect);
      socket.off("ticket", aoAtualizarTicket);
      socket.off("contact", aoAtualizarContato);
      socket.off("tag", aoAtualizarEtiqueta);
    };
  }, [ticketId, history]);

  // Estado da conexao deste atendimento. A lista de conexoes e viva (socket),
  // entao a tela reage na hora quando o numero cai, reconecta ou e excluido.
  const conexoes = Array.isArray(whatsApps) ? whatsApps : [];
  const conexao = ticket.whatsappId
    ? conexoes.find((w) => w.id === ticket.whatsappId)
    : null;

  // Sem conexao: a conexao foi excluida (o vinculo virou nulo) ou ela sumiu da
  // lista com o atendimento ainda apontando para ela.
  const semConexao =
    !!ticket.id && (!ticket.whatsappId || (conexoes.length > 0 && !conexao));

  const nomeDaConexao =
    conexao?.name || ticket.whatsapp?.name || ticket.conexaoAnterior || "";

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };

  return (
    <div className={classes.root} id="drawer-container">
      <Paper
        variant="outlined"
        elevation={0}
        className={clsx(classes.mainWrapper, {
          [classes.mainWrapperShift]: drawerOpen,
        })}
      >
        <TicketHeader loading={loading}>
          <div className={classes.ticketInfo}>
            <TicketInfo
              contact={contact}
              ticket={ticket}
              onClick={handleDrawerOpen}
            />
          </div>
          <div className={classes.ticketActionButtons}>
            <TicketActionButtons ticket={ticket} contact={contact} />
          </div>
        </TicketHeader>
        <ReplyMessageProvider>
          <EditingMessageProvider>
            <MensagensPendentesProvider>
              <MessagesList
                ticketId={ticketId}
                isGroup={ticket.isGroup}
              ></MessagesList>
              <MessageInput
                ticketStatus={ticket.status}
                semConexao={semConexao}
                conexaoNome={nomeDaConexao}
                conexaoStatus={conexao?.status}
              />
            </MensagensPendentesProvider>
          </EditingMessageProvider>
        </ReplyMessageProvider>
      </Paper>
      <ContactDrawer
        open={drawerOpen}
        handleDrawerClose={handleDrawerClose}
        contact={contact}
        ticketId={ticket.id}
        atendente={ticket.user?.name}
        atribuidoPor={ticket.atribuidoPor}
        loading={loading}
        conexaoNome={nomeDaConexao}
        conexaoStatus={conexao?.status}
        semConexao={semConexao}
      />
    </div>
  );
};

export default Ticket;
